
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Users, 
  CreditCard, 
  Calendar, 
  Files, 
  BarChart3, 
  Cloud,
  ChevronRight,
  Hash,
  Target,
  Sparkles,
  Zap,
  Info,
  ShieldCheck,
  UserX,
  History,
  Repeat,
  ArrowRight,
  PlayCircle
} from 'lucide-react';
import { useTranslation } from '../lib/translations';

const Guide: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('intro');

  const sections = [
    {
      id: 'intro',
      icon: Hash,
      title: t.guide.sections.intro.title,
      content: t.guide.sections.intro.content,
      color: 'text-purple-600',
      bg: 'bg-purple-50 dark:bg-purple-900/20'
    },
    {
      id: 'onboarding',
      icon: Users,
      title: t.guide.sections.onboarding.title,
      content: t.guide.sections.onboarding.content,
      tip: t.guide.sections.onboarding.tip,
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      id: 'credits',
      icon: CreditCard,
      title: t.guide.sections.credits.title,
      content: t.guide.sections.credits.content,
      tip: t.guide.sections.credits.tip,
      color: 'text-teal-600',
      bg: 'bg-teal-50 dark:bg-teal-900/20'
    },
    {
      id: 'planner',
      icon: Calendar,
      title: t.guide.sections.planner.title,
      content: t.guide.sections.planner.content,
      tip: t.guide.sections.planner.tip,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50 dark:bg-indigo-900/20'
    },
    {
      id: 'lessons',
      icon: Target,
      title: t.guide.sections.lessons.title,
      content: t.guide.sections.lessons.content,
      tip: t.guide.sections.lessons.tip,
      color: 'text-amber-600',
      bg: 'bg-amber-50 dark:bg-amber-900/20'
    },
    {
      id: 'curriculum',
      icon: Files,
      title: t.guide.sections.curriculum.title,
      content: t.guide.sections.curriculum.content,
      tip: t.guide.sections.curriculum.tip,
      color: 'text-rose-600',
      bg: 'bg-rose-50 dark:bg-rose-900/20'
    },
    {
      id: 'churn',
      icon: UserX,
      title: t.guide.sections.churn.title,
      content: t.guide.sections.churn.content,
      tip: t.guide.sections.churn.tip,
      color: 'text-slate-600',
      bg: 'bg-slate-50 dark:bg-slate-900/20'
    },
    {
      id: 'stats',
      icon: BarChart3,
      title: t.guide.sections.stats.title,
      content: t.guide.sections.stats.content,
      tip: t.guide.sections.stats.tip,
      color: 'text-sky-600',
      bg: 'bg-sky-50 dark:bg-sky-900/20'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5, rootMargin: '-10% 0% -40% 0%' }
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const relaunchTour = () => {
    // Clear localStorage tour flags for all users on this device to be safe
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('tour_completed_')) {
        localStorage.removeItem(key);
      }
    });
    // Set a signal for Dashboard to force-start the tour
    localStorage.setItem('force_show_tour', 'true');
    navigate('/');
  };

  return (
    <div className="max-w-7xl mx-auto pb-32">
      <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-[0.2em] shadow-sm mb-4">
            <Sparkles size={12} className="animate-pulse" /> {t.nav.guide}
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter mb-4">
            {t.guide.title}
          </h1>
          <p className="text-slate-500 font-medium text-lg">
            {t.guide.desc}
          </p>
        </div>

        {/* Tour Relaunch Card */}
        <div className="glass-card p-6 border-purple-500/20 shadow-xl shadow-purple-500/10 max-w-sm w-full group hover:border-purple-500/40 transition-all">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center text-white">
              <PlayCircle size={20} />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white tracking-tight uppercase">{t.tour.relaunch}</h3>
              <p className="text-[10px] text-slate-500 font-bold leading-tight">{t.tour.relaunchDesc}</p>
            </div>
          </div>
          <button 
            onClick={relaunchTour}
            className="w-full py-3 bg-purple-600 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-purple-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
          >
            Start Tour <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-16">
        {/* Sticky Table of Contents */}
        <aside className="lg:w-64 shrink-0 h-fit lg:sticky lg:top-32 hidden lg:block">
          <div className="space-y-6">
            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
              <History size={14} className="text-purple-600" /> {t.guide.toc}
            </h3>
            <nav className="flex flex-col gap-1 border-l border-slate-200 dark:border-white/5 pl-4">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`
                    text-left py-2 text-sm font-bold transition-all
                    ${activeSection === s.id 
                      ? 'text-purple-600 translate-x-1' 
                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'}
                  `}
                >
                  {s.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content Sections */}
        <div className="flex-1 space-y-24">
          {sections.map((section) => (
            <section 
              key={section.id} 
              id={section.id}
              className="scroll-mt-32 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700"
            >
              <div className="flex flex-col md:flex-row gap-8">
                <div className={`shrink-0 w-20 h-20 rounded-3xl ${section.bg} ${section.color} flex items-center justify-center shadow-xl`}>
                  <section.icon size={40} />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                    {section.title}
                  </h2>
                  <div className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed font-medium">
                    {section.content}
                  </div>
                  
                  {section.tip && (
                    <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-white/5 p-6 rounded-2xl flex gap-4 items-start shadow-inner">
                      <div className="p-2 bg-purple-600 rounded-lg text-white shrink-0">
                        <Zap size={16} />
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase tracking-widest text-purple-600">{t.guide.proTip}</p>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300 italic">{section.tip}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="h-px bg-slate-100 dark:border-white/5 w-full"></div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Guide;
